import {
    CSV_EXTENSION,
    EXCEL_EXTENSION,
    JSON_EXTENSION,
    KML_EXTENSION,
    PDF_EXTENSION
} from './../constants/app';
import { IMAGE_FORMATS, IMPORT_FILE_FORMATS } from "../constants/app";
import { PageType } from '../types/core';
import { PAGE } from '../constants/pages';

export function copyToClipboard(text: string) {
    navigator.clipboard.writeText(text);
}

export function getFileExtension(filename: string) {
    const ext = filename.substring(filename.lastIndexOf('.')) || '';
    return ext.toLowerCase();
}

export function isValidPDF(filename: string) {
    const fileExtension = getFileExtension(filename);
    return PDF_EXTENSION === fileExtension;
}

export function isValidImage(filename: string) {
    const fileExtension = getFileExtension(filename);
    return IMAGE_FORMATS.includes(fileExtension);
}

export function isValidYouTube(url: string) {
    url = url.trim().toLowerCase();
    const pattern = /^(https?:\/\/)?(www\.)?(youtu\.be\/|youtube\.com\/(watch\?v=|shorts\/)).*$/;
    return pattern.test(url);
}

export function extractYouTubeID(url: string): string | null {
    if (!isValidYouTube(url)) {
        return null;
    }

    const shortUrlPattern = /youtu\.be\/([a-zA-Z0-9_-]+)/;
    const regularUrlPattern = /youtube\.com\/.*[?&]v=([a-zA-Z0-9_-]+)/;
    const embedUrlPattern = /youtube\.com\/embed\/([a-zA-Z0-9_-]+)/;
    const shortsUrlPattern = /youtube\.com\/shorts\/([a-zA-Z0-9_-]+)/;

    const shortUrlMatch = url.match(shortUrlPattern);
    const regularUrlMatch = url.match(regularUrlPattern);
    const embedUrlMatch = url.match(embedUrlPattern);
    const shortsUrlMatch = url.match(shortsUrlPattern);

    if (shortUrlMatch) {
        return shortUrlMatch[1];
    }
    if (regularUrlMatch) {
        return regularUrlMatch[1];
    }
    if (embedUrlMatch) {
        return embedUrlMatch[1];
    }
    if (shortsUrlMatch) {
        return shortsUrlMatch[1];
    }

    return null;
}

export function isValidImportFile(filename: string, validFormats = IMPORT_FILE_FORMATS) {
    const fileExtension = getFileExtension(filename);
    return validFormats.includes(fileExtension);
}

export function isCsv(filename: string) {
    const fileExtension = getFileExtension(filename);
    return fileExtension === CSV_EXTENSION;
}

export function isExcel(filename: string) {
    const fileExtension = getFileExtension(filename);
    return fileExtension === EXCEL_EXTENSION;
}

export function isJson(filename: string) {
    const fileExtension = getFileExtension(filename);
    return fileExtension === JSON_EXTENSION;
}

export function isKml(filename: string) {
    const fileExtension = getFileExtension(filename);
    return fileExtension === KML_EXTENSION;
}

export function randomString() {
    return Date.now().toString(36) + Math.random().toString(36).substring(2);
}

export function randomNumber(excludedNumbers: number[] = []): number {
    const number = +Math.random().toString().substring(2).slice(0, 5);
    if (excludedNumbers.includes(number)) {
        return randomNumber(excludedNumbers);
    }
    return number;
}

export function isPageType(page: string): page is PageType {
    return Object.values(PAGE).includes(page as PageType);
}

export function isMac() {
    return navigator.userAgent.indexOf('Mac') !== -1;
}

export function isTouchDevice() {
    return ('ontouchstart' in window || navigator.maxTouchPoints > 0 || window.matchMedia("(pointer: coarse)").matches);
}

export function isTestEnvironment() {
    return window.location.hostname !== "live.hellotracks.com";
}

export function isLocalEnvironment() {
    return window.location.hostname === "localhost";
}

export function isDesktopEnvironment() {
    return typeof window !== 'undefined' && !!(window as any).__TAURI__;
}

export async function getDesktopAppVersion() {
    if (!isDesktopEnvironment()) return undefined;
    // works when the global is enabled
    return await (window as any).__TAURI__.app.getVersion();
}

export async function tauriReady(timeoutMs = 4000) {
    const w = window as any;
    if (!w.__TAURI_INTERNALS__ && !w.__TAURI__) return;
    const deadline = Date.now() + timeoutMs;
    while (Date.now() < deadline) {
      if (w.__TAURI_INTERNALS__?.invoke) return;
      await new Promise(r => setTimeout(r, 50));
    }
    throw new Error("Tauri transport not ready");
}

export function isUserFromIndia(): boolean {
    const indianLocales = ["en-IN", "hi-IN", "bn-IN", "te-IN", "mr-IN", "ta-IN", "ur-IN", "gu-IN", "kn-IN", "ml-IN", "pa-IN"];
    const indianTimeZones = ["Asia/Kolkata"];
    const userLocale = navigator.language || (navigator as any).userLanguage;
    const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return indianLocales.includes(userLocale) || indianTimeZones.includes(userTimeZone);
}

export function isPageShowingTools(page: PageType) {
    if (page === PAGE.HEATMAP
        || page === PAGE.REPORTS
        || page === PAGE.STATS
        || page === PAGE.PHOTOS
        || page === PAGE.FORMS
        || page === PAGE.ALERTS
        || page === PAGE.RESELLER
        || page === PAGE.AFFILIATE
        || page === PAGE.REPLAYS // full-width library, no map to act on
        || page === PAGE.PLANNER // full-width week board, no map to act on
        || page === PAGE.TIMESHEET // full-width table, no map to act on
        || page === PAGE.ACTIVITYLOG // full-width table, no map to act on
        || page === PAGE.MESSAGES // full-width inbox + thread; shared locations open the map page
    ) {
        return false;
    }
    return true;
}

export function getDesktopDownloadUrl(): string {
    const baseUrl = "https://storage.googleapis.com/ht-desktop/";
    const version = "1.0.0";
    
    if (isMac()) {
        // For modern Macs, try to detect via performance API or default to ARM for Apple Silicon
        const arch = "aarch64";
        return `${baseUrl}Hellotracks%20Desktop_${version}_${arch}.dmg`;
    } else {
        // Windows
        const arch = "x64";
        return `${baseUrl}Hellotracks%20Desktop_${version}_${arch}-setup.exe`;
    }
}

// works in Tauri v2 (withGlobalTauri enabled) and also in a regular browser
export async function openWindow(url: string, target = "_blank"): Promise<void> {
    const tauri = typeof window !== "undefined" ? (window as any).__TAURI__ : undefined;
  
    // Prefer the Opener plugin in v2
    if (tauri?.opener?.openUrl) {
      await tauri.opener.openUrl(url); // opens in default browser
      return;
    }
  
    // Pure web fallback
    window.open(url, target);
  }
